// 📄 pages/Admin/components/EndpointCollapsibleItem.tsx
// Envuelve EndpointConfigCard.tsx con un header colapsable — solo se usa
// en el Catálogo de APIs Estándar (ver ApiTemplateForm.tsx), no en
// ApiDefinitionCard.tsx (Configuración por cliente), que sigue mostrando
// sus endpoints siempre expandidos tal como antes. Colapsado, el header
// debe alcanzar para identificar el endpoint sin tener que abrirlo.
import { ChevronDown, ChevronUp, Trash2 } from "lucide-react";
import EndpointConfigCard from "../../../components/Configuracion/EndpointConfigCard.tsx";
import { ApiEndpointConfig } from "../../../services/clienteConexionService.ts";

interface EndpointCollapsibleItemProps {
  endpoint: ApiEndpointConfig;
  urlBase: string;
  api: string;
  expandido: boolean;
  onToggle: () => void;
  onChange: (endpoint: ApiEndpointConfig) => void;
  onRemove: () => void;
}

// Mismos colores por método que ya usa AribaApiTester.tsx (METODO_COLOR)
// — se duplica acá en vez de extraerlo a un archivo compartido para no
// tocar esa vista por una constante de 5 líneas.
const METODO_COLOR: Record<string, string> = {
  GET: "bg-emerald-500/20 text-emerald-400",
  POST: "bg-blue-500/20 text-blue-400",
  PUT: "bg-amber-500/20 text-amber-400",
  PATCH: "bg-purple-500/20 text-purple-400",
  DELETE: "bg-red-500/20 text-red-400"
};

const PATH_PREVIEW_MAX = 40;

const truncar = (texto: string, max: number): string => (texto.length > max ? `${texto.slice(0, max)}...` : texto);

export default function EndpointCollapsibleItem({ endpoint, urlBase, api, expandido, onToggle, onChange, onRemove }: EndpointCollapsibleItemProps) {
  const numParams = endpoint.params.filter((p) => p.clave.trim()).length;
  const numHeaders = (endpoint.headers || []).filter((h) => h.clave.trim()).length;

  return (
    <div className="bg-black/20 border border-white/10 rounded-xl overflow-hidden">
      <div className="flex items-center gap-2 p-3">
        <button type="button" onClick={onToggle} className="flex items-center gap-2 min-w-0 flex-1 text-left">
          <span className={`text-[10px] font-extrabold px-1.5 py-0.5 rounded flex-shrink-0 ${METODO_COLOR[endpoint.metodo] || METODO_COLOR.GET}`}>
            {endpoint.metodo}
          </span>
          <span className="text-sm font-medium text-white truncate flex-shrink-0 max-w-[40%]">{endpoint.nombre || "Sin nombre"}</span>
          {endpoint.endpoint && (
            <span className="text-xs text-gray-500 truncate font-mono">· {truncar(endpoint.endpoint, PATH_PREVIEW_MAX)}</span>
          )}
          {numParams > 0 && (
            <span className="text-xs text-gray-500 flex-shrink-0">· {numParams} param{numParams === 1 ? "" : "s"}</span>
          )}
          {numHeaders > 0 && (
            <span className="text-xs text-gray-500 flex-shrink-0">· {numHeaders} cabecera{numHeaders === 1 ? "" : "s"}</span>
          )}
        </button>
        <button
          type="button"
          onClick={onRemove}
          className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors flex-shrink-0"
          title="Eliminar endpoint"
        >
          <Trash2 className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={onToggle}
          className="p-1.5 text-gray-500 hover:text-white hover:bg-white/10 rounded-lg transition-colors flex-shrink-0"
          title={expandido ? "Colapsar" : "Expandir"}
        >
          {expandido ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {expandido && (
        <div className="px-3 pb-3">
          <EndpointConfigCard
            endpoint={endpoint}
            urlBase={urlBase}
            api={api}
            readOnly={false}
            onChange={onChange}
            onRemove={onRemove}
            ocultarBotonEliminar
          />
        </div>
      )}
    </div>
  );
}
